import React, { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Tooltip,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import Papa from 'papaparse';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SendIcon from '@mui/icons-material/Send';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useBulkSendStaging } from '../context/BulkSendStagingContext';
import { buildVmtRecordFromSelection } from '../utils/vmt';

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch (err) {
    console.error('Fallback copy failed', err);
    return false;
  }
};

const buildDateFilter = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

export const buildDateColumn = (
  key,
  header,
  accessor,
  displayKey = `${key}Display`,
  { calendar = false } = {}
) => ({
  accessorKey: key,
  header,
  accessorFn: accessor,
  filterVariant: 'date',
  sortingFn: 'datetime',
  filterFn: (row, id, filterValue) => {
    const targetDate = buildDateFilter(filterValue?.toString());
    if (!targetDate) return true;
    const value = row.getValue(id);
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return false;
    const target = calendar
      ? Date.UTC(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate())
      : targetDate.getTime();
    return date.getTime() >= target;
  },
  Cell: ({ row, cell }) => row.original?.[displayKey] || row.original?.[key] || cell.getValue() || '',
});

const buildStatusChip = (value) => {
  const status = (value || 'unknown').toString();
  const color =
    status === 'success' ? 'success' : status === 'error' ? 'error' : status === 'pending' ? 'info' : 'default';
  return <Chip size="small" label={status} color={color} />;
};

const SendHistoryMRTTable = ({
  rows,
  onRowClick,
  rowSelection,
  onRowSelectionChange,
  onRequestDelete,
  onRequestClear,
  onRefresh,
  isRefreshing,
  onNotify,
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [notesAnchor, setNotesAnchor] = useState(null);
  const [noteTargetRow, setNoteTargetRow] = useState(null);
  const [noteInput, setNoteInput] = useState('');
  const [sorting, setSorting] = useState([{ id: 'sendTimestamp', desc: true }]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const { stageRecord, stageRecords } = useBulkSendStaging();

  const brandOptions = useMemo(() => {
    const codes = new Set();
    rows.forEach((row) => {
      if (Array.isArray(row.brandsCodes)) {
        row.brandsCodes.filter(Boolean).forEach((code) => codes.add(String(code)));
      }
      if (row.brandCd) codes.add(String(row.brandCd));
      if (row.gmBrandCd) codes.add(String(row.gmBrandCd));
      if (row.productBrandCode) codes.add(String(row.productBrandCode));
      if (Array.isArray(row.brandNames)) {
        row.brandNames.filter(Boolean).forEach((name) => codes.add(String(name)));
      }
      if (row.brandsDisplay) codes.add(String(row.brandsDisplay));
    });
    return Array.from(codes).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!selectedBrands.length) return rows;
    const targets = new Set(selectedBrands.map((value) => String(value)));
    return rows.filter((row) => {
      const codes = Array.isArray(row.brandsCodes)
        ? row.brandsCodes.map((code) => String(code)).filter(Boolean)
        : [];
      const names = Array.isArray(row.brandNames)
        ? row.brandNames.map((name) => String(name)).filter(Boolean)
        : [];
      const brandCd = row.brandCd || row.gmBrandCd || row.productBrandCode;
      if (codes.some((code) => targets.has(code))) return true;
      if (names.some((name) => targets.has(name))) return true;
      if (brandCd && targets.has(String(brandCd))) return true;
      const brandsDisplay = row.brandsDisplay ? String(row.brandsDisplay) : '';
      if (brandsDisplay && targets.has(brandsDisplay)) return true;
      return false;
    });
  }, [rows, selectedBrands]);

  const buildVmtContextFromRow = useCallback((row) => ({
    bac: row.bac || '',
    programName: row.programName || row.programLabel || '',
    programCode: row.programCode || '',
    status: row.productSelectionStatus || row.status,
    designation: row.designation,
    type: row.type,
    vendors: row.vendors || [],
    brandCodes: row.brandsCodes || row.brandCodes || [],
    brandNames: row.brandNames || row.brandsDisplay || [],
    brandCd: row.gmBrandCd || row.brandCd || row.brandCode || '',
    brandCode: row.brandCode || row.gmBrandCd || '',
    countryCd: row.countryCd || row.countryCode || '',
    lastUpdatedBy: row.lastUpdatedBy || '',
  }), []);

  const buildEnrollmentInfoFields = useCallback((row) => ({
    dealerName: row?.dealerName || '',
    brands: Array.isArray(row?.brandsDisplay)
      ? row.brandsDisplay.join(', ')
      : row?.brandsDisplay || row?.brandName || '',
  }), []);

  const stageSingleRow = useCallback((row, event, note = '') => {
    if (event) event.stopPropagation();
    if (!row) return;
    const record = buildVmtRecordFromSelection(row, buildVmtContextFromRow(row));
    const trimmedNote = note?.trim();
    record.notes = trimmedNote || record.notes || '';
    if (!record.referenceId) {
      record.referenceId = row.__sendEntry?.id ? `${row.__sendEntry.id}-${row.__recordIndex || 0}` : row.id;
    }
    stageRecord(record, buildEnrollmentInfoFields(row));
    onNotify?.('Selection added to bulk-send staging.', 'success');
  }, [buildEnrollmentInfoFields, buildVmtContextFromRow, onNotify, stageRecord]);

  const stageMultipleRows = useCallback((targets) => {
    if (!Array.isArray(targets) || targets.length === 0) return;
    const payload = targets.map((row) => {
      const record = buildVmtRecordFromSelection(row, buildVmtContextFromRow(row));
      if (!record.referenceId) {
        record.referenceId = row.__sendEntry?.id ? `${row.__sendEntry.id}-${row.__recordIndex || 0}` : row.id;
      }
      return { record, infoFields: buildEnrollmentInfoFields(row) };
    });
    stageRecords(payload);
    onNotify?.(`Staged ${payload.length} selection(s).`, 'success');
  }, [buildEnrollmentInfoFields, buildVmtContextFromRow, onNotify, stageRecords]);

  const openNotesMenu = useCallback((event, row) => {
    event.stopPropagation();
    setNoteTargetRow(row);
    setNotesAnchor(event.currentTarget);
    setNoteInput('');
  }, []);

  const handleCloseNotesMenu = useCallback(() => {
    setNotesAnchor(null);
    setNoteTargetRow(null);
    setNoteInput('');
  }, []);

  const handleSendWithNote = useCallback(() => {
    if (!noteTargetRow) return;
    stageSingleRow(noteTargetRow, null, noteInput);
    handleCloseNotesMenu();
  }, [handleCloseNotesMenu, noteInput, noteTargetRow, stageSingleRow]);
  const columns = useMemo(
    () => [
      {
        id: 'actions',
        header: 'Actions',
        size: 80,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Send selection to staging">
              <IconButton
                aria-label="Send selection to staging"
                size="small"
                onClick={(event) => stageSingleRow(row.original, event)}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Send with note">
              <IconButton
                aria-label="Send selection to staging with note"
                size="small"
                onClick={(event) => openNotesMenu(event, row.original)}
              >
                <NotesIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      { accessorKey: 'bac', header: 'BAC', size: 110, filterVariant: 'text' },
      { accessorKey: 'dealerName', header: 'Dealer Name', size: 200, filterVariant: 'text' },
      { accessorKey: 'programName', header: 'Program', size: 180, filterVariant: 'text' },
      { accessorKey: 'vendorName', header: 'Vendor', size: 180, filterVariant: 'text' },
      { accessorKey: 'categoryName', header: 'Category', size: 160, filterVariant: 'text' },
      { accessorKey: 'productBrandCode', header: 'Product', size: 140, filterVariant: 'text' },
      {
        accessorKey: 'sendStatus',
        header: 'Send Status',
        size: 120,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: 'success', value: 'success' },
          { text: 'error', value: 'error' },
          { text: 'pending', value: 'pending' },
        ],
        Cell: ({ cell }) => buildStatusChip(cell.getValue()),
      },
      buildDateColumn('sendTimestamp', 'Sent At', (row) => row.sendTimestamp || row.sendTimestampDisplay || ''),
      buildDateColumn(
        'productSelectionDate',
        'Selection Date',
        (row) => row.productSelectionDateSort || row.productSelectionDate || row.productSelectionDateDisplay || '',
        undefined,
        { calendar: true }
      ),
      buildDateColumn(
        'goLiveDate',
        'Go Live Date',
        (row) => row.goLiveDateSort || row.goLiveDate || '',
        undefined,
        { calendar: true }
      ),
      buildDateColumn(
        'billingStartDate',
        'Billing Start',
        (row) => row.billingStartDateSort || row.billingStartDate || '',
        undefined,
        { calendar: true }
      ),
      buildDateColumn(
        'billingEndDate',
        'Billing End',
        (row) => row.billingEndDateSort || row.billingEndDate || '',
        undefined,
        { calendar: true }
      ),
      buildDateColumn('terminationDate', 'Termination Date', (row) => row.terminationDateSort || row.terminationDate || ''),
      { accessorKey: 'brandsDisplay', header: 'Brands', size: 180, filterVariant: 'text' },
      { accessorKey: 'productSelectionStatus', header: 'Selection Status', size: 180, filterVariant: 'text' },
    ],
    [openNotesMenu, stageSingleRow]
  );

  const resolveCellValue = (row, column) => {
    if (typeof column.accessorFn === 'function') return column.accessorFn(row);
    if (column.accessorKey) return row[column.accessorKey];
    return '';
  };

  const table = useMaterialReactTable({
    columns,
    data: filteredRows,
    enableRowSelection: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    enableDensityToggle: true,
    enableGlobalFilter: true,
    enableColumnFilterModes: true,
    enableFacetedValues: true,
    enableStickyHeader: true,
    enablePagination: true,
    localization: MRT_Localization_EN,
    initialState: {
      density: 'compact',
      pagination: { pageSize: 25, pageIndex: 0 },
      showColumnFilters: true,
    },
    getRowId: (row) => row.id,
    state: { rowSelection, columnFilters, sorting, globalFilter },
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: onRowSelectionChange,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    enableSortingRemoval: true,
    muiTableBodyRowProps: ({ row }) => ({
      hover: true,
      sx: { cursor: 'pointer' },
      onClick: () => onRowClick?.(row.original),
    }),
    renderTopToolbarCustomActions: ({ table }) => {
      const selectedRows = table.getSelectedRowModel().flatRows.map((flat) => flat.original);
      const dealerFilterValue = columnFilters.find((item) => item.id === 'dealerName')?.value || '';
      const programFilterValue = columnFilters.find((item) => item.id === 'programName')?.value || '';
      const hasRows = (selectedRows.length > 0 ? selectedRows : filteredRows).length > 0;
      const updateFilter = (id, value) => {
        table.setColumnFilters((prev) => {
          const next = prev.filter((item) => item.id !== id);
          if (value) {
            next.push({ id, value });
          }
          return next;
        });
      };
      const handleCopy = async () => {
        const payload = selectedRows.length > 0 ? selectedRows : filteredRows;
        if (!payload.length) {
          onNotify?.('No rows available to copy.', 'info');
          return;
        }
        await copyToClipboard(JSON.stringify(payload, null, 2));
        onNotify?.(`Copied ${payload.length} row(s) as JSON.`, 'success');
      };

      const handleCopyTsv = async () => {
        const payload = selectedRows.length > 0 ? selectedRows : filteredRows;
        if (!payload.length) {
          onNotify?.('No rows available to copy.', 'info');
          return;
        }
        const exportableColumns = table
          .getAllLeafColumns()
          .filter((col) => col.id !== 'mrt-row-expand' && col.id !== 'select' && col.columnDef?.header);
        const headers = exportableColumns.map((col) => col.columnDef.header);
        const rowsText = payload
          .map((row) => exportableColumns.map((col) => resolveCellValue(row, col.columnDef) ?? '').join('\t'))
          .join('\n');
        await copyToClipboard(`${headers.join('\t')}\n${rowsText}`);
        onNotify?.(`Copied ${payload.length} row(s) as TSV.`, 'success');
      };
      const handleExport = () => {
        const payload = selectedRows.length > 0 ? selectedRows : filteredRows;
        if (!payload.length) {
          onNotify?.('No rows available to export.', 'info');
          return;
        }
        const csv = Papa.unparse(payload);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'send-history.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        onNotify?.(`Exported ${payload.length} row(s) to CSV.`, 'success');
      };
      const handleResetFilters = () => {
        table.resetColumnFilters();
        table.resetGlobalFilter();
        setSelectedBrands([]);
      };
      const handleStageSelected = () => {
        const payload = selectedRows.length > 0 ? selectedRows : filteredRows;
        stageMultipleRows(payload);
      };
      const sortOldestFirst = () => setSorting([{ id: 'sendTimestamp', desc: false }]);
      const sortNewestFirst = () => setSorting([{ id: 'sendTimestamp', desc: true }]);
      return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', py: 1 }}>
          <TextField
            label="Dealer filter"
            size="small"
            value={dealerFilterValue}
            onChange={(event) => updateFilter('dealerName', event.target.value)}
            inputProps={{ 'data-testid': 'toolbar-filter-dealerName' }}
          />
          <TextField
            label="Program filter"
            size="small"
            value={programFilterValue}
            onChange={(event) => updateFilter('programName', event.target.value)}
            inputProps={{ 'data-testid': 'toolbar-filter-programName' }}
          />
          <TextField
            label="Global search"
            size="small"
            value={globalFilter}
            onChange={(event) => table.setGlobalFilter(event.target.value)}
            inputProps={{ 'data-testid': 'toolbar-global-search' }}
          />
          <Autocomplete
            multiple
            options={brandOptions}
            value={selectedBrands}
            onChange={(_event, value) => setSelectedBrands(value)}
            disableCloseOnSelect
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                label="Brands"
                size="small"
                inputProps={{ ...params.inputProps, 'data-testid': 'toolbar-filter-brands' }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip {...tagProps} key={key || option} label={option} size="small" />;
              })
            }
            sx={{ minWidth: 240 }}
            clearIcon={false}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<FilterAltIcon />}
            onClick={handleResetFilters}
            aria-label="Reset filters"
          >
            Reset Filters
          </Button>
          <Tooltip title="Copy selected rows as JSON or fall back to all rows">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
                aria-label="Copy rows"
              >
                Copy JSON
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Copy selected rows (or all) as TSV">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCopyTsv}
                aria-label="Copy TSV"
              >
                Copy TSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export selected rows (or all) to CSV">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                aria-label="Export CSV"
                disabled={!hasRows}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowDownwardIcon />}
            onClick={sortNewestFirst}
            aria-label="Sort newest first"
          >
            Newest
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowUpwardIcon />}
            onClick={sortOldestFirst}
            aria-label="Sort oldest first"
          >
            Oldest
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refresh send history"
          >
            {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SendIcon />}
            onClick={handleStageSelected}
            aria-label="Stage selected"
          >
            Stage Selected
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteForeverIcon />}
            onClick={onRequestDelete}
            disabled={selectedRows.length === 0}
            aria-label="Delete selected history"
          >
            Delete Selected
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={onRequestClear}
            aria-label="Clear all history"
          >
            Clear History
          </Button>
        </Box>
      );
    },
    muiTableHeadCellFilterTextFieldProps: ({ column }) => ({
      'aria-label': 'Filter column',
      'data-testid': `filter-${column.id}`,
    }),
    enableSorting: true,
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <Menu
        anchorEl={notesAnchor}
        open={Boolean(notesAnchor)}
        onClose={handleCloseNotesMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ px: 2, py: 1, maxWidth: 320 }}>
          <TextField
            label="Add note"
            size="small"
            fullWidth
            value={noteInput}
            onChange={(event) => setNoteInput(event.target.value)}
            inputProps={{ 'data-testid': 'staging-note-input' }}
          />
        </Box>
        <Divider />
        <MenuItem onClick={handleSendWithNote} aria-label="Send to staging with note">
          <SendIcon fontSize="small" sx={{ mr: 1 }} /> Send with note
        </MenuItem>
      </Menu>
    </>
  );
};

export default SendHistoryMRTTable;
